import { useEffect, useRef, useState } from "react";
import { Bell, CheckCircle2, Euro, Package, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

import { _t, _locale } from "@/lib/i18n";
function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "adesso";
  if (diff < 3600) return _t("{0} min fa", Math.floor(diff / 60));
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return d.toLocaleDateString(_locale(), { day: "2-digit", month: "short" });
}

function isPaymentNotif(kind) {
  return typeof kind === "string" && kind.startsWith("payment_");
}

/**
 * NotificationsBell — dropdown popover accessible from the header bell.
 *
 * Highlights notifications generated by confirmed payments (kind starts with
 * `payment_`) with a €-badge. These notifications are created server-side
 * ONLY after Stripe webhook (or verified poll) confirms `payment_status=paid`
 * — never from the redirect to /payment/success. See payments.py `_handle_paid`.
 *
 * Filter tabs: all | payments | others.
 */
export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | payments | others
  const ref = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = items.filter((n) => {
    if (filter === "payments") return isPaymentNotif(n.kind);
    if (filter === "others") return !isPaymentNotif(n.kind);
    return true;
  });
  const unread = items.filter((n) => !n.read).length;
  const paymentUnread = items.filter((n) => !n.read && isPaymentNotif(n.kind)).length;

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, read: true } : x)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1"
        title={_t("Notifiche")}
        data-testid="notifications-bell"
      >
        <Bell className="w-5 h-5 text-white/70" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5722] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread}
          </span>
        )}
        {paymentUnread > 0 && (
          <span
            data-testid="notification-payment-dot"
            className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0B1220]"
            title={`${paymentUnread} pagamento/i confermato/i`}
          />
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-3 w-[380px] max-h-[520px] overflow-hidden rounded-xl border border-white/10 bg-[#0B1220] shadow-xl z-50"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="font-display text-white text-lg">{_t("Notifiche")}</div>
            <button onClick={load} className="text-white/50 hover:text-white text-xs" data-testid="notifications-refresh">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : _t("Aggiorna")}
            </button>
          </div>
          <div className="flex border-b border-white/10 bg-white/[0.02]">
            {[
              { key: "all", label: _t("Tutte"), count: items.length },
              { key: "payments", label: _t("Pagamenti"), count: items.filter((n) => isPaymentNotif(n.kind)).length },
              { key: "others", label: _t("Altre"), count: items.filter((n) => !isPaymentNotif(n.kind)).length },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                data-testid={`notifications-filter-${t.key}`}
                className={`flex-1 text-xs px-3 py-2 border-b-2 transition-colors ${
                  filter === t.key
                    ? "border-[#FF5722] text-white"
                    : "border-transparent text-white/50 hover:text-white/80"
                }`}
              >
                {t.label} <span className="text-white/40">({t.count})</span>
              </button>
            ))}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="text-center text-white/40 text-sm py-10">{_t("Nessuna notifica")}</div>
            )}
            {filtered.map((n) => {
              const pay = isPaymentNotif(n.kind);
              return (
                <div
                  key={n.id}
                  data-testid={`notification-item${pay ? "-payment" : ""}`}
                  onClick={() => !n.read && markRead(n.id)}
                  className={`px-4 py-3 border-b border-white/5 cursor-pointer transition-colors ${
                    n.read ? "hover:bg-white/[0.03]" : "hover:bg-white/[0.05]"
                  }`}
                  style={pay && !n.read ? { background: _t("rgba(16,185,129,0.06)") } : {}}
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5">
                      {pay ? (
                        <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                          <Euro className="w-4 h-4 text-emerald-400" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                          <Package className="w-4 h-4 text-white/60" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className={`text-sm font-medium ${n.read ? "text-white/70" : "text-white"}`}>
                          {n.title}
                        </div>
                        {pay && !n.read && (
                          <span className="text-[9px] uppercase tracking-widest text-emerald-400 border border-emerald-400/30 rounded-full px-1.5 py-[1px]">{_t("confermato")}</span>
                        )}
                      </div>
                      <div className={`text-xs mt-1 ${n.read ? "text-white/40" : "text-white/60"}`}>{n.body}</div>
                      <div className="text-[10px] text-white/40 mt-1">{fmtRelative(n.created_at)}</div>
                    </div>
                    {!n.read && (
                      <div className="mt-1">
                        <span className="w-2 h-2 rounded-full bg-[#FF5722] inline-block" />
                      </div>
                    )}
                    {n.read && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-white/20 mt-1" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
